import { isValidDate } from "@/src/utils/is-valid-date";

export const simpleDateFormatter = (
  dateString: string,
  dateFormat?: object | undefined,
) => {
  const date: Date = new Date(dateString);
  const defaultDateFormat = {
    year: "numeric",
    month: "short",
    day: "2-digit",
  };
  const formattedDate = date
    .toLocaleDateString("en-US", dateFormat || defaultDateFormat)
    .replace(/\//g, "-");

  return formattedDate;
};

// Publish dates reach the index as full UTC timestamps ("2026-01-22T00:00:00.000000Z")
// while exports want the calendar day alone. Read the day off the string instead of
// through a Date, whose local-timezone parsing shifts it west of UTC.
export const isoDateFormatter = (dateString?: string | null): string => {
  if (!dateString) return "";

  const [day] = String(dateString).split("T");

  return /^\d{4}-\d{2}-\d{2}$/.test(day) &&
    isValidDate(new Date(`${day}T00:00:00Z`))
    ? day
    : "";
};

export const lastUpdateFormatter = (dateString: string) => {
  const date = new Date(dateString);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const month = months[date.getUTCMonth()];
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();

  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");

  return `${day} ${month}, ${year} | ${hours}:${minutes}`;
};

// Weekday + 2-digit month + year, e.g. "Tuesday 07,2026". Note this deliberately
// omits the day of month, matching the format the post cards have always rendered.
export const weekdayMonthYearFormatter = (dateString: string): string => {
  const date = new Date(dateString);
  if (!isValidDate(date)) return "";

  const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
  const month = String(date.getMonth() + 1).padStart(2, "0");

  return `${weekday} ${month},${date.getFullYear()}`;
};

export const publishedTimeFormatter = (
  dateString: string,
): string | undefined => {
  const date = new Date(dateString);
  return isValidDate(date) ? date.toISOString() : undefined;
};

export const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (diffSeconds < 60) return "just now";
  if (diffMinutes < 60)
    return `${diffMinutes} minute${diffMinutes !== 1 ? "s" : ""} ago`;
  if (diffHours < 24)
    return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
  if (diffWeeks < 4)
    return `${diffWeeks} week${diffWeeks !== 1 ? "s" : ""} ago`;
  if (diffMonths < 12)
    return `${diffMonths} month${diffMonths !== 1 ? "s" : ""} ago`;
  return `${diffYears} year${diffYears !== 1 ? "s" : ""} ago`;
};
